import type { GapRowType, HiringGapsGrid, HiringMetricKey, MetricRowKey } from "../types";
import { buildGapToneFn, type GapTone } from "./gapTones";
import { cellKey, DAYS_PER_WEEK } from "./suggestedHires";

/** Every grid the Missing gap is composed from. `appliedGrid` is the client-side suggested-hire overlay. */
export type HiringGridBundle = {
	neededGrid: HiringGapsGrid;
	actualGrid: HiringGapsGrid;
	appliedGrid?: HiringGapsGrid;
	structuralOtGrid: HiringGapsGrid;
	internalPickupOtGrid: HiringGapsGrid;
	internalPickupNonOtGrid: HiringGapsGrid;
	absencesGrid: HiringGapsGrid;
};

/** The Count levers. A metric that is off contributes 0 — it is still reported, as excluded. */
export type IncludeFlags = Record<HiringMetricKey, boolean>;

export const gridVal = (grid: HiringGapsGrid | undefined, label: string, key: number): number => (grid ? (grid[label]?.[key] ?? 0) : 0);

/** One cell's terms, each already gated by its Count lever. Feeds both the number and its tooltip. */
export type MissingTerms = {
	needed: number;
	/** Recurring from the department's real schedule template. */
	template: number;
	/** Recurring added by suggested hires the user applied — a render-time overlay, never persisted. */
	applied: number;
	structuralOt: number;
	pickupOt: number;
	pickupNonOt: number;
	absences: number;
};

export const cellTerms = (grids: HiringGridBundle, includeFlags: IncludeFlags, label: string, key: number): MissingTerms => ({
	needed: gridVal(grids.neededGrid, label, key),
	template: gridVal(grids.actualGrid, label, key),
	applied: gridVal(grids.appliedGrid, label, key),
	structuralOt: includeFlags.structural_ot ? gridVal(grids.structuralOtGrid, label, key) : 0,
	pickupOt: includeFlags.internal_pickup_ot ? gridVal(grids.internalPickupOtGrid, label, key) : 0,
	pickupNonOt: includeFlags.internal_pickup_non_ot ? gridVal(grids.internalPickupNonOtGrid, label, key) : 0,
	absences: includeFlags.absences ? gridVal(grids.absencesGrid, label, key) : 0,
});

/**
 * The "Missing" gap: needed + structural_ot − internal_pickup_ot − internal_pickup_non_ot − recurring
 * + absences, minus the applied hires.
 *
 * Structural OT (band-aid coverage) ADDS; real internal pickups SUBTRACT; agency is excluded entirely.
 * Absences ADD: a recurring nurse who was absent is still counted in `recurring`, so adding absences back
 * undoes that phantom coverage.
 *
 * THE one copy in the frontend — the table's cells, its tooltips, and the still-short markers on a
 * suggested hire's day chips all read it, so a chip cannot claim a day is fine while the cell shows red.
 * MUST mirror the BE `_build_recommendation_diff_grid` exactly — keep the two in sync.
 */
export const missingFromTerms = (terms: MissingTerms): number =>
	terms.needed + terms.structuralOt - terms.pickupOt - terms.pickupNonOt - terms.template - terms.applied + terms.absences;

/**
 * A Missing value as the user should read it: `-N` when short, `+N` when over. The direction lives in
 * the TEXT, not only in the colour — the old code printed a bare "0" for both a covered and an
 * overstaffed cell, which is what made a surplus look like an unexplained warning. `0` is the balanced
 * case and the cell renders a check glyph instead of calling this.
 */
export const formatMissing = (missing: number): string => (missing > 0 ? `-${missing}` : `+${-missing}`);

export const cellMissing = (grids: HiringGridBundle, includeFlags: IncludeFlags, label: string, key: number): number =>
	missingFromTerms(cellTerms(grids, includeFlags, label, key));

/** Shift label → every cell key in the displayed window → that cell's Missing. */
export type MissingGrid = Record<string, Record<number, number>>;

export const shiftLabelsOf = (grids: HiringGridBundle): string[] => [...new Set([...Object.keys(grids.neededGrid), ...Object.keys(grids.actualGrid)])];

export const buildMissingGrid = (grids: HiringGridBundle, includeFlags: IncludeFlags, gridWeeks: number): MissingGrid => {
	const missingGrid: MissingGrid = {};
	for (const label of shiftLabelsOf(grids)) {
		const row: Record<number, number> = {};
		for (let weekIdx = 0; weekIdx < gridWeeks; weekIdx++) {
			for (let dayIdx = 0; dayIdx < DAYS_PER_WEEK; dayIdx++) {
				const key = cellKey(weekIdx, dayIdx);
				row[key] = cellMissing(grids, includeFlags, label, key);
			}
		}
		missingGrid[label] = row;
	}
	return missingGrid;
};

/**
 * One tone function per shift label, so a shift is ranked against its OWN worst days — a quiet 11p-7a
 * isn't graded by an 8a-4p outlier.
 */
export const buildMissingToneFns = (missingGrid: MissingGrid): Record<string, (missing: number) => GapTone> => {
	const map: Record<string, (missing: number) => GapTone> = {};
	for (const [label, row] of Object.entries(missingGrid)) {
		map[label] = buildGapToneFn(Object.values(row));
	}
	return map;
};

/**
 * Which rows a shift block renders, top to bottom.
 *
 * Detailed view reads as the running math — Needed, Recurring, the active metric breakdown, Missing.
 * Collapsed view shows only the composed Missing number.
 *
 * Recurring only appears when Detailed View is on. The `X + Y` split is informative, but in collapsed
 * view it was a second row that asked to be read when the Missing number alone is the decision point.
 */
export const gapsRowTypes = (detailedView: boolean, metricKeys: readonly MetricRowKey[]): GapRowType[] => {
	if (detailedView) {
		return ["needed", "recurring", ...metricKeys, "missing"];
	}
	return ["missing"];
};
